import React, { useEffect, useState } from "react";
import { Col, Spinner } from "reactstrap";
import { congRoles } from "utility/constants";
import {
  fetchAllAgencies,
  fetchAllAgencyProjects,
} from "services/services.projects";
import _ from "lodash";
import { Toast, getFormattedOptions } from "utility/helpers";
import { useLocation, useNavigate } from "react-router";
import { genericGet, genericPost } from "services/services.general";
import {
  updateUtilizationReport,
  fetchOthersList,
} from "services/services.utilization";
import useAuth from "hooks/useAuth";
import LoadingScreen from "components/customs/loading-screen";
import { AddButton, CancelButton, HeaderText } from "assets/js/Styles";
import styled from "styled-components";
import { VIEW_ACTION } from "constants/actions";
import { congressmanSearch } from "services/services.navbar";
import { PRINCIPAL_IDS, getFormText } from "../constants";
import { validate } from "../utility";
import UtilizationFormSection from "./UtilizationFormSection";

const BtnSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 3rem;
  padding-bottom: 4rem;
  gap: 16px;
`;

const StyledAddButton = styled(AddButton)`
  background: ${({ theme }) => theme.colors.buttonFormBlue};
  width: 130px;
`;

const StyledCancelButton = styled(CancelButton)`
  width: 130px;
`;

const MainContainer = styled.div`
  margin-top: 50px;
`;

const HeaderTextSection = styled.div`
  margin-top: 1.5rem;
  display: grid;
  justify-content: space-between;
  grid-auto-flow: column;
  align-items: center;

  h2 {
    margin-bottom: 0;
  }

  a {
    text-decoration: none;
  }

  @media (max-width: 575.98px) {
    grid-template-columns: 1fr 50px;

    a {
      display: flex;
      justify-content: flex-end;
    }

    padding-top: 5rem;
  }
`;

const UtilizationForm = () => {
  const location = useLocation();
  const { user } = useAuth();

  const row = location.state?.row || null;
  const { action } = location.state;

  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedExcel, setUploadedExcel] = useState([]);
  const [agencyOptions, setAgencyOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [othersOptions, setOthersOptions] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedPrincipal, setSelectedPrincipal] = useState(null);
  const [selectedOthers, setSelectedOthers] = useState(null);
  const [dateImplemented, setDateImplemented] = useState(null);
  const [totalCost, setTotalCost] = useState(null);
  const [isProjectsFetched, setIsProjectsFetched] = useState(false);
  const [displayOthersFilter, setDisplayOthersFilter] = useState(false);
  const [disablePrincipal, setDisablePrincipal] = useState(false);

  const [form, setForm] = useState({
    title: "",
    venue: "",
    dateImplemented,
    numberOfBeneficiary: 0,
    costPerBeneficiary: 0,
    remarks: "",
    partners: "",
    attachments: [],
    congressmanId: 0,
    agencyId: 0,
    projectId: 0,
  });

  const navigate = useNavigate();

  const fetchAgencies = () => {
    fetchAllAgencies({
      callback: (data) => {
        const newData = getFormattedOptions("name", data);
        setAgencyOptions(newData);
      },
    });
  };

  const fetchProjects = () => {
    fetchAllAgencyProjects({
      url: `/agency/${selectedAgency?.value}`,
      callback: (data) => {
        let projects = data?.Projects;

        if (selectedAgency?.value === 14) {
          projects = data?.Projects.filter(
            (project) => project.name === "Infrastructure"
          );
        }

        const options = getFormattedOptions("name", projects);
        if (selectedAgency?.value === 14) setSelectedProject(options[0]);
        setProjectOptions(options);
        setIsProjectsFetched(true);
      },
    });
  };

  const fetchCongDetails = (congName) => {
    genericGet({
      url: `/congressman?search=${congName}`,
      callback: (data) => {
        const options = {
          ...data,
          label: data[0].name,
          value: data[0].id,
        };

        setSelectedPrincipal(options);

        if (PRINCIPAL_IDS.includes(data[0].id)) {
          setDisplayOthersFilter(true);
        }
      },
    });
  };

  const fetchOthers = (congId) => {
    fetchOthersList({
      url: `/utilization/others/list?congressmanId=${congId}`,

      callback: (data) => {
        const newData = getFormattedOptions("allocationContactName", data);
        setOthersOptions(newData);

        if (row) {
          const rowOthers = newData.find(
            (others) => others.allocationContactName === row.principalOthers
          );
          setSelectedOthers(rowOthers);
        }
      },
    });
  };

  const handleCongressmanSearch = _.debounce((inputValue, callback) => {
    congressmanSearch({
      inputValue,
      callback,
    });
  }, 1000);

  const saveData = (formData) => {
    genericPost({
      url: "/utilization",
      formData,
      callback: ({ isSuccess }) => {
        setIsLoading(false);

        if (isSuccess) {
          navigate("/utilization");

          Toast({
            type: 1,
            content: "Successfully created utilization report.",
          });
        } else {
          Toast({
            type: 4,
            content: "An error occurred.",
          });
        }
      },
    });
  };

  const updateData = (formData, id) => {
    updateUtilizationReport({
      param: id,
      url: `/utilization/${id}`,
      formData,
      callback: ({ isSuccess }) => {
        if (isSuccess) {
          setTimeout(() => {
            Toast({
              type: 1,
              content: "Successfully updated utilization report.",
            });
          }, 1500);

          navigate("/utilization");
        } else {
          Toast({
            type: 4,
            content: "An error occurred.",
          });
        }
      },
    });
  };

  const handleSendOnClicked = async () => {
    const isValid = await validate(
      form,
      dateImplemented,
      selectedAgency,
      selectedProject,
      selectedPrincipal,
      uploadedExcel,
      uploadedFiles
    );

    if (typeof isValid === "boolean") {
      setIsLoading(true);

      const formData = {
        title: form.title,
        venue: form.venue,
        dateImplemented,
        numberOfBeneficiary: form.numberOfBeneficiary,
        costPerBeneficiary: form.costPerBeneficiary,
        remarks: form.remarks,
        partners: form.partners,
        attachments: uploadedFiles?.map((file) => file.location),
        congressmanId: selectedPrincipal.value,
        agencyId: selectedAgency.value,
        projectId: selectedProject.value,
        principalOthers: selectedOthers?.allocationContactName,
        xlsxLinks: uploadedExcel?.map((file) => file.location),
        fbLinks: form.fbLinks,
      };

      if (row === null) {
        saveData(formData);
      } else {
        updateData(formData, row.id);
      }
    } else {
      Toast({
        type: 4,
        content: isValid,
      });
    }
  };

  const setFromData = () => {
    setForm({
      title: row.title,
      venue: row.venue,
      numberOfBeneficiary: row.numberOfBeneficiary,
      costPerBeneficiary: row.costPerBeneficiary,
      remarks: row.remarks,
      partners: row.partners,
      fbLinks: row.fbLinks,
    });

    const total = row.numberOfBeneficiary * row.costPerBeneficiary;
    const parsedDate = new Date(row.dateImplemented);
    setDateImplemented(parsedDate);
    setTotalCost(total);

    fetchCongDetails(row.Congressman.fullName);
    fetchOthers(row.congressmanId);

    const files = [];
    const excels = [];

    if (row?.attachments?.length !== 0) {
      row?.attachments?.forEach((fileURL) => {
        const fileArray = fileURL.split("/");

        const file = {
          name: decodeURIComponent(fileArray[fileArray.length - 1]),
          location: fileURL,
          percent: 100,
        };

        files.push(file);
      });
    }

    if (row?.xlsxLinks?.length !== 0) {
      row?.xlsxLinks?.forEach((fileURL) => {
        const fileArray = fileURL.split("/");

        const file = {
          name: decodeURIComponent(fileArray[fileArray.length - 1]),
          location: fileURL,
          percent: 100,
        };

        excels.push(file);
      });
    }

    setUploadedFiles(files);
    setUploadedExcel(excels);
  };

  useEffect(() => {
    //  fetch all agencies
    fetchAgencies();

    if (congRoles.includes(user?.type)) {
      setDisablePrincipal(true);
      fetchCongDetails(user?.congressman?.name);
    }
  }, []);

  useEffect(() => {
    if (selectedAgency) {
      // reset selected project on agency change
      setSelectedProject(null);
      fetchProjects();
    }
  }, [selectedAgency]);

  useEffect(() => {
    if (row && agencyOptions.length !== 0) {
      const rowAgency = agencyOptions.find(
        (agency) => agency.id === row.agencyId
      );
      setSelectedAgency(rowAgency);
    }
  }, [row, agencyOptions]);

  useEffect(() => {
    if (row && isProjectsFetched) {
      const rowProgram = projectOptions.find(
        (project) => project.id === row.projectId
      );
      setSelectedProject(rowProgram);
    }
  }, [row, isProjectsFetched]);

  useEffect(() => {
    if (row) {
      setFromData();
    }
  }, [row]);

  if (!user) {
    return <LoadingScreen />;
  }

  return (
    <MainContainer>
      <Col className="container">
        <HeaderTextSection>
          <HeaderText>{getFormText(action)}</HeaderText>
        </HeaderTextSection>

        <UtilizationFormSection
          form={form}
          setForm={setForm}
          action={action}
          handleCongressmanSearch={handleCongressmanSearch}
          selectedPrincipal={selectedPrincipal}
          setSelectedPrincipal={setSelectedPrincipal}
          setDisplayOthersFilter={setDisplayOthersFilter}
          fetchOthers={fetchOthers}
          disablePrincipal={disablePrincipal}
          agencyOptions={agencyOptions}
          selectedAgency={selectedAgency}
          setSelectedAgency={setSelectedAgency}
          projectOptions={projectOptions}
          selectedProject={selectedProject}
          setSelectedProject={setSelectedProject}
          displayOthersFilter={displayOthersFilter}
          othersOptions={othersOptions}
          selectedOthers={selectedOthers}
          setSelectedOthers={setSelectedOthers}
          setTotalCost={setTotalCost}
          totalCost={totalCost}
          dateImplemented={dateImplemented}
          setDateImplemented={setDateImplemented}
          uploadedExcel={uploadedExcel}
          setUploadedExcel={setUploadedExcel}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
        />

        <BtnSection>
          <StyledCancelButton
            outline
            disabled={isLoading}
            onClick={() => navigate("/utilization")}
          >
            {"Back"}
          </StyledCancelButton>

          <StyledAddButton
            color="primary"
            onClick={() => handleSendOnClicked()}
            disabled={isLoading || action === VIEW_ACTION}
          >
            {isLoading ? (
              <Spinner
                animation="border"
                color="light"
                size="sm"
              />
            ) : (
              "Save"
            )}
          </StyledAddButton>
        </BtnSection>
      </Col>
    </MainContainer>
  );
};

export default UtilizationForm;
